// hooks/useOattUpload.ts
import { useState, useCallback, useRef, useEffect } from 'react';
import useSession from './useSession';
import { getRuntimeEnv } from '@utils/runtimeEnv';
import { appInsights } from '../appInsights';

const API_BASE =
  getRuntimeEnv('REACT_APP_OATT_API_BASE') ||
  `${getRuntimeEnv('REACT_APP_SERVER_PROTOCOL')}://${getRuntimeEnv('REACT_APP_SERVER_HOST')}/api`;

const SCAN_STATUS_POLL_INTERVAL_MS = 5_000;

export interface OattValidationError {
  aref: string | null;
  fileLineNumber: number | null;
  field: string;
  value: string;
  reason: string;
}

export interface UploadFileResult {
  filename: string;
  success: boolean;
  created?: number;
  updated?: number;
  skipped?: number;
  errors?: { row: number; message: string }[];
  message?: string;
  validationErrors?: OattValidationError[];
  /** File was accepted and uploaded to blob storage; still awaiting the malware scan result. */
  pending?: boolean;
  /** File was blocked and removed because it was flagged as malicious. */
  malicious?: boolean;
  status?: string;
}

export interface UploadState {
  uploading: boolean;
  /** True while any uploaded file is still PendingScan (virus scan in progress). */
  scanning: boolean;
  results: UploadFileResult[];
  error: string | null;
}

interface ScanStatusFile {
  fileName: string;
  status: string;
  errorMessage?: string | null;
}

const IDLE_STATE: UploadState = { uploading: false, scanning: false, results: [], error: null };

export function useOattUpload() {
  const { accessToken } = useSession();
  const [state, setState] = useState<UploadState>(IDLE_STATE);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // Polls GET /oatt/scan-status/:batchId until every file that was pending after
  // the initial upload resolves to Clean or Malicious, merging each outcome into
  // `results` as it arrives. Resolves the returned promise once fully settled so
  // callers (e.g. the ingestion-log refetch) run only after data actually exists.
  const pollScanStatus = useCallback((batchId: string, onSettled: () => void) => {
    const poll = async () => {
      try {
        const response = await fetch(`${API_BASE}/oatt/scan-status/${batchId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) return; // transient — keep polling

        const body = await response.json();
        const files: ScanStatusFile[] = body?.data?.files ?? [];
        if (files.length === 0) return;

        const stillPending = files.some((f) => f.status === 'PendingScan');

        setState((prev) => ({
          ...prev,
          results: prev.results.map((r) => {
            if (!r.pending) return r;
            const match = files.find((f) => f.fileName === r.filename);
            if (!match) return r;
            if (match.status === 'Malicious') {
              return {
                filename: r.filename,
                success: false,
                malicious: true,
                message: 'Malicious file detected — upload blocked and file removed.',
              };
            }
            if (match.status === 'Clean') {
              return { ...r, pending: false, status: 'Clean' };
            }
            return r; // still PendingScan
          }),
        }));

        if (!stillPending) {
          stopPolling();
          setState((prev) => ({ ...prev, scanning: false }));
          onSettled();
        }
      } catch {
        // Transient failure — keep polling until the interval retries.
      }
    };

    poll();
    pollRef.current = setInterval(poll, SCAN_STATUS_POLL_INTERVAL_MS);
  }, [accessToken, stopPolling]);

  const uploadFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    stopPolling();
    setState({ uploading: true, scanning: false, results: [], error: null });

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      const response = await fetch(`${API_BASE}/oatt/upload`, {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const contentType = response.headers.get('content-type') ?? '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server error (${response.status}): ${text}`);
      }

      const data = await response.json();

      if (!response.ok && response.status !== 207) {
        throw new Error(data.message || `Upload failed (${response.status})`);
      }

      const results: UploadFileResult[] = data.results ?? [];
      const hasPending = results.some((r) => r.pending);

      setState({ uploading: false, scanning: hasPending, results, error: null });

      appInsights?.trackEvent({
        name: results.every((r) => r.success) ? 'OattUploadSuccess' : 'OattUploadFailure',
        properties: { fileCount: results.length, successCount: results.filter((r) => r.success).length },
      });

      if (!hasPending || !data.batchId) return data;

      // Wait for the malware scan (and, once Clean, the DB insert) to finish
      // before resolving, so callers know when it's safe to refetch dependent data.
      await new Promise<void>((resolve) => pollScanStatus(data.batchId, resolve));
      return data;
    } catch (err: any) {
      setState({ uploading: false, scanning: false, results: [], error: err.message });
      appInsights?.trackEvent({ name: 'OattUploadFailure', properties: { error: err.message } });
      throw err;
    }
  }, [accessToken, pollScanStatus, stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setState(IDLE_STATE);
  }, [stopPolling]);

  return { ...state, uploadFiles, reset };
}
